import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './config';
import './Strategies.css';

const PERIODS = [
  { key: 'day', label: 'Dag' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Maand' },
  { key: 'ytd', label: 'YTD' },
  { key: 'year', label: 'Jaar' },
];

// Voor nu alleen trades vanaf juli 2026 ter review voorleggen — alles ervoor is bewust
// leeggemaakt (geen voorstel) en blijft met rust totdat dit verder is uitgerold.
const REVIEW_CUTOFF = '2026-07-01';

function fmtEur(n) {
  if (n === null || n === undefined) return '–';
  const v = Number(n);
  return (v < 0 ? '-€' : '+€') + Math.abs(v).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function pct(n) {
  if (n === null || n === undefined) return '–';
  return `${n}%`;
}

/* ---------- Review tab: fast queue to confirm open-strategy proposals and close-discipline verdicts ---------- */
function ReviewTab({ strategies }) {
  const [mode, setMode] = useState('open'); // 'open' | 'close'
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pickerOpenFor, setPickerOpenFor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = mode === 'open'
      ? { open_status: 'pending_review', min_open_date: REVIEW_CUTOFF, ordering: 'created_at', page_size: 25 }
      : { close_status: 'pending_review', min_close_date: REVIEW_CUTOFF, ordering: 'created_at', page_size: 25 };
    axios.get(`${API_URL}/portfolio/trade-strategy-logs/`, { params })
      .then(res => setQueue(res.data.results || res.data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [mode]);

  useEffect(() => { load(); }, [load]);

  const current = queue[0];

  const confirmOpen = (log, strategyId) => {
    axios.post(`${API_URL}/portfolio/trade-strategy-logs/${log.id}/confirm_open/`, { strategy_id: strategyId })
      .then(() => {
        setQueue(q => q.slice(1));
        setPickerOpenFor(null);
      });
  };

  const confirmClose = (log, followed) => {
    axios.post(`${API_URL}/portfolio/trade-strategy-logs/${log.id}/confirm_close/`, { discipline_followed: followed })
      .then(() => setQueue(q => q.slice(1)));
  };

  return (
    <div className="strat-tab">
      <p className="strat-muted">Alleen trades vanaf {REVIEW_CUTOFF} — oudere trades zijn voorlopig leeg gelaten.</p>
      <div className="strat-subtabs">
        <button className={mode === 'open' ? 'active' : ''} onClick={() => setMode('open')}>Nog te taggen (open)</button>
        <button className={mode === 'close' ? 'active' : ''} onClick={() => setMode('close')}>Nog te beoordelen (discipline)</button>
        <button className="strat-refresh" onClick={load} title="Vernieuwen">⟳</button>
      </div>

      {loading && <p className="strat-muted">Laden...</p>}
      {error && <p className="strat-error">{error}</p>}
      {!loading && !current && <p className="strat-muted">Niks meer in deze wachtrij. 🎉</p>}

      {current && mode === 'open' && (
        <div className="strat-card">
          <div className="strat-card-head">
            <span className="strat-symbol">{current.opening_trade.symbol}</span>
            <span className="strat-date">{current.opening_trade.trade_date_parsed}</span>
            <span className="strat-buysell">{current.opening_trade.buy_sell}</span>
          </div>
          <p className="strat-reasoning">{current.proposal_reasoning}</p>
          <div className="strat-actions">
            <button className="strat-btn primary" onClick={() => confirmOpen(current, current.proposed_strategy)}>
              ✓ Bevestig: {current.proposed_strategy_name}
            </button>
            <button className="strat-btn" onClick={() => setPickerOpenFor(pickerOpenFor === current.id ? null : current.id)}>
              Andere strategie...
            </button>
          </div>
          {pickerOpenFor === current.id && (
            <div className="strat-picker">
              {strategies.map(s => (
                <button key={s.id} className="strat-picker-option" onClick={() => confirmOpen(current, s.id)}>
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {current && mode === 'close' && (
        <div className="strat-card">
          <div className="strat-card-head">
            <span className="strat-symbol">{current.underlying_symbol} {current.option_symbol}</span>
            <span className="strat-date">{current.closing_trade?.trade_date_parsed}</span>
            <span className={`strat-pnl ${Number(current.closing_trade?.fifo_pnl_realized) >= 0 ? 'pos' : 'neg'}`}>
              {fmtEur(current.closing_trade?.fifo_pnl_realized)}
            </span>
          </div>
          <p className="strat-strategy-label">Strategie: {current.strategy_name || current.proposed_strategy_name || '–'}</p>
          <p className="strat-reasoning">{current.close_discipline_reasoning}</p>
          <div className="strat-actions">
            <button className="strat-btn primary" onClick={() => confirmClose(current, true)}>✓ Discipline gevolgd</button>
            <button className="strat-btn danger" onClick={() => confirmClose(current, false)}>✗ Regel niet gevolgd</button>
          </div>
        </div>
      )}

      {!loading && queue.length > 1 && (
        <p className="strat-muted strat-queue-count">{queue.length - 1} nog te gaan na deze (van deze batch van 25)</p>
      )}
    </div>
  );
}

/* ---------- Confirmed tab: simple table of everything with a confirmed strategy ---------- */
function ConfirmedTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/portfolio/trade-strategy-logs/`, {
      params: { open_status: 'confirmed', ordering: '-created_at', page_size: 100 },
    }).then(res => setRows(res.data.results || res.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="strat-tab">
      {loading && <p className="strat-muted">Laden...</p>}
      {!loading && rows.length === 0 && <p className="strat-muted">Nog geen bevestigde trades.</p>}
      {!loading && rows.length > 0 && (
        <div className="strat-table-wrap">
          <table className="strat-table">
            <thead>
              <tr>
                <th>Symbool</th><th>Open</th><th>Strategie</th><th>Sluit</th><th>P&amp;L</th><th>Discipline</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td>{r.opening_trade.symbol}</td>
                  <td>{r.opening_trade.trade_date_parsed}</td>
                  <td>{r.strategy_name}</td>
                  <td>{r.closing_trade ? r.closing_trade.trade_date_parsed : '– nog open –'}</td>
                  <td className={r.closing_trade && Number(r.closing_trade.fifo_pnl_realized) >= 0 ? 'pos' : 'neg'}>
                    {r.closing_trade ? fmtEur(r.closing_trade.fifo_pnl_realized) : '–'}
                  </td>
                  <td>
                    {r.close_discipline_followed === true ? '✓' : r.close_discipline_followed === false ? '✗' : '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------- Small building blocks for the overview: stat tile with a win-rate bar,
   and a stacked discipline-vs-outcome bar. Semantic status colors only (win=green,
   loss=red) — consistent with the rest of the app's pos/neg convention, never used
   for series identity. ---------- */
function StrategyStatTile({ s }) {
  const wr = s.win_rate_pct ?? 0;
  const barColor = wr >= 60 ? 'var(--strat-good)' : wr >= 45 ? 'var(--strat-warn)' : 'var(--strat-bad)';
  return (
    <div className="strat-tile">
      <div className="strat-tile-head">
        <span className="strat-tile-name">{s.strategy}</span>
        <span className={`strat-tile-pnl ${s.total_pnl >= 0 ? 'pos' : 'neg'}`}>{fmtEur(s.total_pnl)}</span>
      </div>
      <div className="strat-tile-bar-row">
        <div className="strat-tile-bar-track">
          <div className="strat-tile-bar-fill" style={{ width: `${Math.min(100, wr)}%`, background: barColor }} />
        </div>
        <span className="strat-tile-pct">{pct(s.win_rate_pct)}</span>
      </div>
      <div className="strat-tile-meta">
        <span>{s.trades} trades ({s.wins}W/{s.losses}L)</span>
        <span>Discipline {pct(s.discipline_rate_pct)}</span>
      </div>
    </div>
  );
}

function DisciplineChart({ strategies }) {
  const totals = strategies.reduce((acc, s) => ({
    win_disc: acc.win_disc + s.win_and_disciplined,
    loss_disc: acc.loss_disc + Math.max(0, s.discipline_followed - s.win_and_disciplined),
    win_undisc: acc.win_undisc + s.win_and_undisciplined,
    loss_undisc: acc.loss_undisc + Math.max(0, s.discipline_broken - s.win_and_undisciplined),
  }), { win_disc: 0, loss_disc: 0, win_undisc: 0, loss_undisc: 0 });

  const groups = [
    { label: 'Discipline gevolgd', win: totals.win_disc, loss: totals.loss_disc },
    { label: 'Discipline niet gevolgd', win: totals.win_undisc, loss: totals.loss_undisc },
  ];
  const maxTotal = Math.max(1, ...groups.map(g => g.win + g.loss));

  return (
    <div className="strat-discipline-chart">
      <div className="strat-chart-legend">
        <span><i className="dot win" /> Winstgevend gesloten</span>
        <span><i className="dot loss" /> Verlies gesloten</span>
      </div>
      {groups.map(g => {
        const total = g.win + g.loss;
        return (
          <div className="strat-chart-row" key={g.label}>
            <span className="strat-chart-label">{g.label}</span>
            <div className="strat-chart-track" style={{ width: `${(total / maxTotal) * 100}%` }}>
              {total > 0 && (
                <>
                  <div className="strat-chart-seg win" style={{ width: `${(g.win / total) * 100}%` }} title={`${g.win} winst`} />
                  <div className="strat-chart-seg loss" style={{ width: `${(g.loss / total) * 100}%` }} title={`${g.loss} verlies`} />
                </>
              )}
            </div>
            <span className="strat-chart-count">{g.win}W / {g.loss}L{total > 0 ? ` (${Math.round(100 * g.win / total)}% winst)` : ''}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Overview tab: per-period stats per strategy ---------- */
function OverviewTab() {
  const [period, setPeriod] = useState('ytd');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/portfolio/trade-strategy-logs/overview/`, { params: { period, date } })
      .then(res => setData(res.data))
      .finally(() => setLoading(false));
  }, [period, date]);

  return (
    <div className="strat-tab">
      <div className="strat-subtabs">
        {PERIODS.map(p => (
          <button key={p.key} className={period === p.key ? 'active' : ''} onClick={() => setPeriod(p.key)}>{p.label}</button>
        ))}
        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="strat-date-input" />
      </div>

      {loading && <p className="strat-muted">Laden...</p>}
      {!loading && data && (
        <>
          <p className="strat-muted">{data.start_date} t/m {data.end_date}</p>

          {data.strategies.length === 0 && <p className="strat-muted">Geen gesloten, getagde trades in deze periode.</p>}

          {data.strategies.length > 0 && (
            <>
              <div className="strat-tiles">
                {data.strategies.map(s => <StrategyStatTile key={s.strategy} s={s} />)}
              </div>

              <h3 className="strat-section-title">Discipline vs. resultaat</h3>
              <p className="strat-muted">Van alle trades in deze periode: hielp discipline volgen, en hoeveel ging alsnog goed of fout zonder?</p>
              <DisciplineChart strategies={data.strategies} />

              <h3 className="strat-section-title">Per strategie in detail</h3>
              <div className="strat-table-wrap">
                <table className="strat-table">
                  <thead>
                    <tr>
                      <th>Strategie</th>
                      <th>Trades</th>
                      <th>Winratio</th>
                      <th>Discipline</th>
                      <th>Netto P&amp;L</th>
                      <th title="Van de trades waar de regel NIET gevolgd is, hoeveel waren alsnog winstgevend">Toch winst zonder discipline</th>
                      <th title="Van de trades waar de regel WEL gevolgd is, hoeveel waren winstgevend">Winst mét discipline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.strategies.map(s => (
                      <tr key={s.strategy}>
                        <td>{s.strategy}</td>
                        <td>{s.trades} ({s.wins}W/{s.losses}L)</td>
                        <td>{pct(s.win_rate_pct)}</td>
                        <td>{pct(s.discipline_rate_pct)} ({s.discipline_broken}x niet)</td>
                        <td className={s.total_pnl >= 0 ? 'pos' : 'neg'}>{fmtEur(s.total_pnl)}</td>
                        <td>{s.win_and_undisciplined} / {s.discipline_broken}</td>
                        <td>{s.win_and_disciplined} / {s.discipline_followed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default function Strategies() {
  const [searchParams] = useSearchParams();
  const submenu = searchParams.get('submenu') || 'review';
  const [strategies, setStrategies] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/portfolio/trade-strategies/`, { params: { page_size: 'all' } })
      .then(res => setStrategies(res.data.results || res.data));
  }, []);

  return (
    <div className="strategies-page">
      {submenu === 'review' && <ReviewTab strategies={strategies} />}
      {submenu === 'confirmed' && <ConfirmedTab />}
      {submenu === 'overview' && <OverviewTab />}
    </div>
  );
}
